import type { CaptiveDevice } from '../api/captive';

/**
 * Human-readable identity qualifier for a registered device — the strongest
 * form stored (MAC + Option 82, MAC + switch/port, or MAC alone), used in the
 * de-register choice list so a subscriber can tell his devices apart.
 */
export function deviceQualifier(d: CaptiveDevice): string {
  if (d.opt82_circuit || d.opt82_remote) {
    return [d.opt82_circuit, d.opt82_remote].filter(Boolean).join(' / ');
  }
  if (d.trap_switch_id) {
    return d.trap_port ? `sw#${d.trap_switch_id} / порт ${d.trap_port}` : `sw#${d.trap_switch_id}`;
  }
  return '';
}

/* ------------------------------------------------------------------------- *
 * Money.
 *
 * Every amount on the wire is a DECIMAL STRING (`money.Amount`, e.g. "1000.00").
 * These helpers work on the string and NEVER convert it to a float: a float would
 * silently re-round the operator's kopecks, and the portal shows the very numbers
 * the charge is computed from.
 * ------------------------------------------------------------------------- */

const RUBLE = '₽';
const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

/** Thin-space-free thousands grouping of the integer part, string-only. */
function groupDecimalString(value: string): string {
  const neg = value.startsWith('-');
  const unsigned = neg ? value.slice(1) : value;
  const [intPart, fracPart] = unsigned.split('.');
  const grouped = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  const body = fracPart !== undefined ? `${grouped}.${fracPart}` : grouped;
  return neg ? `-${body}` : body;
}

/** Pad a decimal string to exactly-or-more than two fraction digits. */
function withTwoDecimals(value: string): string {
  if (!value.includes('.')) return `${value}.00`;
  const frac = value.split('.')[1];
  return frac.length < 2 ? `${value}${'0'.repeat(2 - frac.length)}` : value;
}

/**
 * Format a decimal-string amount for a subscriber: grouped integer part, two
 * decimals, ruble sign — `"1000" → "1 000.00 ₽"`. Absent/empty ⇒ `"0.00 ₽"`; an
 * unparseable value is shown verbatim rather than as a wrong number.
 */
export function formatRubles(value?: string | null): string {
  const raw = (value ?? '').trim();
  if (raw === '') return `0.00 ${RUBLE}`;
  if (!DECIMAL_RE.test(raw)) return raw;
  return `${groupDecimalString(withTwoDecimals(raw))} ${RUBLE}`;
}

/**
 * True when a decimal-string amount is strictly greater than zero — the test for
 * "there is a shortfall" / "there is something to charge". Digit-based, so it is
 * exact for any precision and immune to float rounding.
 */
export function isPositiveMoney(value?: string | null): boolean {
  const raw = (value ?? '').trim();
  if (raw === '' || raw.startsWith('-')) return false;
  return /[1-9]/.test(raw);
}

/**
 * True when a decimal-string amount is strictly LESS than zero — i.e. the subscriber
 * owes that much. `"-0.00"` is not a debt.
 */
export function isNegativeMoney(value?: string | null): boolean {
  const raw = (value ?? '').trim();
  return raw.startsWith('-') && /[1-9]/.test(raw);
}

/**
 * Absolute value of a decimal-string amount — the amount to PAY when the balance is
 * negative. String-only, so no float ever re-rounds the operator's kopecks.
 */
export function absMoney(value?: string | null): string {
  const raw = (value ?? '').trim();
  return raw.startsWith('-') ? raw.slice(1) : raw;
}

/** Format an ISO-8601 timestamp for display; empty/absent → null. */
export function formatTime(iso?: string | null): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString('ru-RU', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}
